import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Lock, ArrowLeft, Crown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { CompoundCard } from "@/components/CompoundCard";
import { AiSummary, DoseNote } from "@/components/evidence/shared";
import { ShareButtons } from "@/components/ShareButtons";

const setMeta = (title, desc) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
  m.content = desc;
};

const Locked = ({ data, t }) => (
  <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-white p-6" data-testid="condition-public-locked">
    <div className="mb-4 flex flex-wrap gap-2 blur-[3px] select-none" aria-hidden>
      {data.locked_names.map((n, i) => <span key={i} className="rounded-full bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700">{n}</span>)}
    </div>
    <div className="text-center">
      <Lock className="mx-auto mb-2 text-amber-500" size={26} />
      <p className="mb-1 font-bold text-gray-900">+{data.locked_count} {t("cpub.more")}</p>
      <p className="mb-4 text-sm text-gray-600">{t("cpub.moreSub")}</p>
      <Link to="/pricing" data-testid="condition-public-unlock-cta" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-600"><Crown size={15} /> {t("cpub.unlock")}</Link>
    </div>
  </div>
);

export default function ConditionPublic() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/conditions/${slug}/preview`, { params: { language: lang } })
      .then(({ data }) => {
        setData(data);
        setMeta(`${t("cpub.metaTitle")} ${data.condition} | Suppev`,
          `${data.compounds.map((c) => c.name).join(", ")}… ${t("cpub.metaDesc")} ${data.condition}.`);
      })
      .catch(() => setErr(true));
  }, [slug, lang, t]);

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="condition-public-page">
      <Link to="/evidence" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:underline"><ArrowLeft size={15} /> {t("ev.title")}</Link>
      {err && <p className="text-sm text-gray-600">{t("cond.none")}</p>}
      {!data && !err && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={15} className="animate-spin" /> {t("cond.analyzing")}</p>}
      {data && (
        <>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="condition-public-title">
            {t("cpub.h1")} {data.condition}
          </h1>
          <p className="text-sm text-gray-600">{data.study_count} {t("cpub.studies")}</p>
          <ShareButtons t={t} message={`${t("share.msg")} ${data.condition}:`} />
          <AiSummary text={data.ai_summary} stage={data.evidence_stage} t={t} />
          <DoseNote t={t} />
          <div className="space-y-3">{data.compounds.map((c, i) => <CompoundCard key={i} c={c} i={i} t={t} />)}</div>
          {data.locked_count > 0 && <Locked data={data} t={t} />}
          <ShareButtons t={t} message={`${t("share.msg")} ${data.condition}:`} />
        </>
      )}
    </div>
  );
}
